import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, BACKEND, fmtTime, fmtBytes } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, UrlRow, Pill, usePoll } from "@/components/common";
import { Download, RotateCcw, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TZS = ["America/New_York", "America/Chicago", "America/Los_Angeles", "UTC", "Europe/London", "Europe/Berlin", "Africa/Accra", "Africa/Lagos", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Australia/Sydney"];

function Backups() {
  const { data, reload } = usePoll("/admin/config/backups", 15000);
  const now = async () => { try { await api.post("/admin/config/backups"); toast.success("Backup saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const restore = async (b) => {
    if (!window.confirm(`Replace the current config with the backup from ${fmtTime(b.ts, true)}?`)) return;
    try { const { data: r } = await api.post(`/admin/config/backups/${b.id}/restore`, { mode: "replace" }); toast.success(`Restored: ${Object.entries(r.counts).map(([k, v]) => `${v} ${k}`).join(", ")}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (b) => { await api.delete(`/admin/config/backups/${b.id}`); reload(); };
  return (
    <Panel title="Saved backups" testid="saved-backups" right={<Button data-testid="backup-now-btn" size="sm" variant="ghost" onClick={now}>Back up now</Button>}>
      {(data || []).map((b) => (
        <div key={b.id} data-testid={`backup-row-${b.id}`} className="flex items-center gap-3 py-1.5 border-b border-slate-800/60 text-xs">
          <span className="font-mono text-slate-300 w-36">{fmtTime(b.ts, true)}</span>
          <Pill tone={b.trigger === "scheduled" ? "blue" : "slate"}>{b.trigger}</Pill>
          <span className="flex-1 font-mono text-slate-500">{Object.entries(b.counts).map(([k, v]) => `${v} ${k}`).join(" · ")} · {fmtBytes(b.size)}</span>
          <a data-testid={`backup-download-${b.id}`} href={`${BACKEND}/api/admin/config/backups/${b.id}/download`} className="text-slate-400 hover:text-cyan-300"><Download size={13} /></a>
          <button data-testid={`backup-restore-${b.id}`} onClick={() => restore(b)} className="text-slate-400 hover:text-amber-300"><RotateCcw size={13} /></button>
          <button data-testid={`backup-delete-${b.id}`} onClick={() => del(b)} className="text-slate-400 hover:text-red-400"><Trash2 size={13} /></button>
        </div>
      ))}
      {data && data.length === 0 && <p className="text-sm text-slate-500">No saved backups yet. The daily backup runs at 2:00 America/New_York.</p>}
    </Panel>
  );
}

function Schedules({ s, setS }) {
  const n = "w-24 h-8 bg-[#0B111B] border-slate-700 font-mono text-xs";
  return (
    <Panel title="Scheduled tasks" testid="schedules-panel">
      <div className="space-y-3 text-sm text-slate-300">
        <div className="flex flex-wrap items-center gap-3">
          <Switch data-testid="sched-tests-switch" checked={!!s.scheduled_tests_enabled} onCheckedChange={(v) => setS({ ...s, scheduled_tests_enabled: v })} />
          <span className="w-64">Nightly acceptance run <span className="font-mono text-xs text-slate-500">03:00 New York</span></span>
          <span className="text-xs text-slate-500">soak</span><Input data-testid="sched-soak-input" type="number" min={10} className={n} value={s.scheduled_soak_seconds} onChange={(e) => setS({ ...s, scheduled_soak_seconds: Number(e.target.value) })} />
          <span className="text-xs text-slate-500">failback</span><Input data-testid="sched-failback-input" type="number" min={10} className={n} value={s.scheduled_failback_window} onChange={(e) => setS({ ...s, scheduled_failback_window: Number(e.target.value) })} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Switch data-testid="sched-backups-switch" checked={!!s.backups_enabled} onCheckedChange={(v) => setS({ ...s, backups_enabled: v })} />
          <span className="w-64">Daily config backup <span className="font-mono text-xs text-slate-500">02:00 New York</span></span>
          <span className="text-xs text-slate-500">keep last</span><Input data-testid="sched-keep-input" type="number" min={1} max={90} className={n} value={s.backup_keep} onChange={(e) => setS({ ...s, backup_keep: Number(e.target.value) })} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Switch data-testid="sched-digest-switch" checked={!!s.digest_enabled} onCheckedChange={(v) => setS({ ...s, digest_enabled: v })} />
          <span className="w-64">Weekly failure digest <span className="font-mono text-xs text-slate-500">Mon 09:00 New York</span></span>
          <span className="text-xs text-slate-500">covering last</span><Input data-testid="sched-digest-days-input" type="number" min={1} max={90} className={n} value={s.digest_days} onChange={(e) => setS({ ...s, digest_days: Number(e.target.value) })} /><span className="text-xs text-slate-500">days</span>
        </div>
        <p className="text-[11px] text-slate-500">Tick "Weekly failure digest" on the Alerts page to receive the digest.</p>
        <p className="text-[11px] text-slate-500">The nightly run's result goes out through Alerts — tick "Acceptance report" on the Alerts page to receive it by webhook or Telegram.</p>
      </div>
    </Panel>
  );
}

function BackupPanel() {
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (mode === "replace" && !window.confirm("Replace ALL channels, servers, providers, lab sources and settings with this backup?")) return;
    setBusy(true);
    try {
      const data = JSON.parse(await file.text());
      const { data: r } = await api.post("/admin/config/import", { data, mode });
      toast.success(`Restored: ${Object.entries(r.counts).map(([k, v]) => `${v} ${k}`).join(", ")}`);
    } catch (x) { toast.error(x instanceof SyntaxError ? "File is not valid JSON" : errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Config backup & restore" testid="backup-panel">
      <p className="text-xs text-slate-400 mb-3">One JSON file with all channels (incl. RTMP keys), authorized servers, providers, lab sources and settings. Keep it private. Restoring briefly restarts every channel (a few seconds of no output).</p>
      <div className="flex flex-wrap items-center gap-3">
        <a data-testid="config-export-btn" href={`${BACKEND}/api/admin/config/export`} className="inline-flex items-center h-9 px-3 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold">Export backup</a>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="import-mode-select" className="w-56 h-9 bg-[#0B111B] border-slate-700 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#16202E] border-slate-700">
            <SelectItem value="replace">Replace everything</SelectItem>
            <SelectItem value="merge">Merge (add / update by id)</SelectItem>
          </SelectContent>
        </Select>
        <label className={`inline-flex items-center h-9 px-3 rounded border border-slate-700 text-sm text-slate-200 cursor-pointer hover:bg-slate-800 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          {busy ? "Restoring…" : "Restore from file…"}
          <input data-testid="config-import-input" type="file" accept="application/json,.json" className="hidden" onChange={onFile} />
        </label>
      </div>
    </Panel>
  );
}

export default function SettingsPage() {
  const { updateTz } = useAuth();
  const [s, setS] = useState(null);
  const [deny, setDeny] = useState("");
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setS(data); setDeny((data.deny_list || []).join("\n")); });
    api.get("/whoami").then(({ data }) => setMe(data)).catch(() => {});
  }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", { ...s, deny_list: deny.split(/[\s,]+/).filter(Boolean) });
      setS(data); updateTz(data.display_tz); toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-3xl space-y-4">
      <PageHeader title="Settings" right={<Button data-testid="settings-save-btn" onClick={save} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">Save settings</Button>} />
      <Panel title="Display timezone">
        <p className="text-xs text-slate-400 mb-2">Times are stored in UTC and shown in this timezone.</p>
        <Select value={s.display_tz} onValueChange={(v) => setS({ ...s, display_tz: v })}>
          <SelectTrigger data-testid="tz-select" className="w-72 bg-[#0B111B] border-slate-700 font-mono text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#16202E] border-slate-700">{TZS.map((t) => <SelectItem key={t} value={t} className="font-mono text-xs">{t}</SelectItem>)}</SelectContent>
        </Select>
      </Panel>
      <Panel title="Global deny list">
        <p className="text-xs text-slate-400 mb-2">IPs or CIDR ranges that are always refused, even if they belong to an authorized server.</p>
        <Textarea data-testid="deny-list-input" rows={4} className="bg-[#0B111B] border-slate-700 font-mono text-xs" value={deny} onChange={(e) => setDeny(e.target.value)} />
      </Panel>
      <Panel title="Your connection">
        <UrlRow label="Your IP" url={me?.ip || "…"} testid="whoami-ip" />
        <p className="text-[11px] text-slate-500 mt-2">Client IPs are read from the hosting platform's forwarding header. Add a downstream server's egress IP in Authorized servers.</p>
      </Panel>
      <Schedules s={s} setS={setS} />
      <BackupPanel />
      <Backups />
      <Panel title="API">
        <UrlRow label="Health" url={`${BACKEND}/api/admin/health`} testid="api-health" />
        <UrlRow label="Metrics" url={`${BACKEND}/api/admin/metrics`} testid="api-metrics" />
        <UrlRow label="Status" url={`${BACKEND}/api/status/<slug>`} testid="api-status" />
        <p className="text-[11px] text-slate-500 mt-2">Health, metrics, channel state and forced switch need the admin session. Per-channel status is open to authorized servers granted that channel.</p>
      </Panel>
    </div>
  );
}
